import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChartConfig,
  ChartId,
  DashboardId,
  GetChartUsageResponse
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { AlertDialog } from '@Components/AlertDialog';
import { Design, Intent } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { copyRoleLevels, saveChart } from '../Charts/chart-save';
import { useChartEditGate } from '../Charts/hooks/useChartEditGate';
import { ChartEditExits } from '../Charts/ChartEditExits';
import { NoEditAccessDialog } from '../Charts/NoEditAccessDialog';
import { FetchError } from '../../utils/fetch-error';
import { chartDataKeyPrefix, chartEnvelopeKey } from '../Charts/hooks/useChartData';
import { chartFansOut, useChartUsage } from '../Charts/hooks/useChartUsage';
import { repointDashboardCard } from './hooks/useDashboard';
import {
  SaveAsNewPlacement,
  SaveAsNewPlacementType,
  SaveChartAsNewDialog
} from './SaveChartAsNewDialog';

// Name the dashboards a chart is on, for the fan-out confirm: "Growth Weekly", "Growth Weekly and 1
// other". Mirrors the single-chart view's describeUsage (kept local — it's a 4-line formatter).
const describeUsage = (usage: GetChartUsageResponse): string => {
  const [first, ...rest] = usage.dashboards;
  if (!first) return 'other dashboards';
  if (rest.length === 0) return first.name;
  return `${first.name} and ${conditionallyPluralizeWithCount(rest.length, 'other')}`;
};

interface DashboardCardEditControlsProps {
  company: string;
  dashboardId: DashboardId;
  chartId: ChartId;
  cardIndex: number;
  workingCopy: ChartConfig;
  onClear: () => void;
  // Whether this user may change the DASHBOARD. Only used to decide whether "Show it here" is on
  // offer when saving a copy — repointing a card rewrites the layout and saves it.
  canEditDashboard: boolean;
}

// The three exits for a dashboard card carrying an unsaved working copy — the dashboard-view analogue
// of the single-chart view's exits, drawn by the same ChartEditExits group (Save ▾ / Discard). Shown
// only when a working copy is present. Reuses the same save primitives (saveChart) and fan-out gate
// (useChartUsage/chartFansOut) as the single-chart exits.
export const DashboardCardEditControls = ({
  company,
  dashboardId,
  chartId,
  cardIndex,
  workingCopy,
  onClear,
  canEditDashboard
}: DashboardCardEditControlsProps) => {
  const queryClient = useQueryClient();
  const setError = useSetError();
  const [saving, setSaving] = useState(false);
  const [showFanOutConfirm, setShowFanOutConfirm] = useState(false);
  const [showSaveAsNew, setShowSaveAsNew] = useState(false);

  // This card's CHART: whether this user may change it, its two role levels and its owner's name.
  // Note this is a different question from `canEditDashboard` above — a user may be free to change a
  // dashboard's layout while being unable to change one of the charts on it, and the reverse. The
  // gate reads the same cache entry the card's title and date picker already populate, so it costs
  // no request, and it is the SAME hook the single-chart view's Update uses.
  const {
    envelope,
    userRole,
    isWriteBlocked,
    showNoEditAccess,
    setShowNoEditAccess,
    handleForbidden
  } = useChartEditGate(company, chartId);

  // The chart's blast radius — which dashboards reference it and whether the user owns them — drives
  // the fan-out confirm. Always enabled here: this only renders for a saved chart with a pending edit.
  const { data: usage } = useChartUsage(company, chartId, true);

  // Re-render the card off its now-updated saved row (the preview already compiled + ran the config, so
  // no data flashes). Invalidating both the render query and the config query catches every date/kpi
  // override variant and refreshes the focus-context config for the next edit. The envelope goes
  // first because the render query reads its config from the envelope cache.
  const invalidateChart = async () => {
    await queryClient.invalidateQueries({ queryKey: chartEnvelopeKey(company, chartId) });
    await queryClient.invalidateQueries({ queryKey: chartDataKeyPrefix(company, chartId) });
  };

  // Update everywhere it's used: persist the working copy in place (same id), refresh the chart, then
  // clear the working copy so the card snaps to the now-updated saved chart.
  const performUpdate = async () => {
    // Defensive. `handleUpdate` gates this, but the fan-out confirmation's main action calls this
    // function DIRECTLY, so a locked chart must not reach a write through that path.
    if (isWriteBlocked) return;
    setSaving(true);
    try {
      await saveChart(company, workingCopy);
      await invalidateChart();
      onClear();
    } catch (e) {
      // The server refused it, and the server is the authority — route to the same dialog the gate
      // would have shown (see useChartEditGate). The `finally` below closes the fan-out confirm on
      // every exit from this function, including this one.
      if (e instanceof FetchError && e.status === 403) {
        await handleForbidden();
        return;
      }
      setError({ message: `Couldn't update this chart: ${e.message}`, reportError: e });
    } finally {
      setSaving(false);
      setShowFanOutConfirm(false);
    }
  };

  // The Update gate: interpose the fan-out confirm when the chart is on a dashboard the user doesn't
  // solely own; otherwise update straight away. Same gate the single-chart view uses.
  const handleUpdate = () => {
    // THE EDIT CHECK RUNS FIRST, before the fan-out check below — the same order the single-chart
    // view uses, and for the same reason: the fan-out dialog's main action calls `performUpdate`
    // directly, so a locked chart reaching that dialog would skip the gate and surface a raw 403.
    if (isWriteBlocked) {
      setShowNoEditAccess(true);
      return;
    }
    if (chartFansOut(usage)) {
      setShowFanOutConfirm(true);
      return;
    }
    performUpdate();
  };

  // Save as a new chart: mint a fresh chart from the working copy under the chosen name, then either
  // repoint THIS card at it ("Show it here") or leave the dashboard on the original ("Just save"). Clear
  // the working copy either way so the card re-renders from a saved row.
  const handleSaveAsNew = async (name: string, placement: SaveAsNewPlacementType) => {
    // The copy inherits this chart's audience, so the envelope has to be in hand. Without it the
    // save would fall through to the backend's create default and publish the copy at the widest
    // view level — and this is the path the no-edit-access dialog sends people down, so it would
    // widen the audience of precisely the charts someone had narrowed. Refuse instead.
    if (!envelope) {
      setError({ message: "Couldn't read this chart's sharing settings. Reload and try again." });
      return;
    }
    setSaving(true);
    try {
      const draft: Omit<ChartConfig, 'id'> & { id?: ChartId } = { ...workingCopy, title: name };
      delete draft.id;
      // Inherit the audience, own the edit rights — the same rule the single-chart view's copy
      // follows, from the same helper, so the two surfaces cannot mint differently-governed copies
      // of the same chart. See `copyRoleLevels` for why the edit level is floored.
      const { id: newId } = await saveChart(company, draft, copyRoleLevels(envelope, userRole));
      if (placement === SaveAsNewPlacement.HERE) {
        await repointDashboardCard(company, dashboardId, { chartId, index: cardIndex }, newId);
        await queryClient.invalidateQueries({ queryKey: ['dashboard', company, dashboardId] });
      }
      // Newly minted chart's metadata should be named (not a bare id) in the agent's focus context.
      await queryClient.invalidateQueries({ queryKey: ['dashboarding-bootstrap', company] });
      onClear();
      setShowSaveAsNew(false);
    } catch (e) {
      setError({ message: `Couldn't save a new chart: ${e.message}`, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ChartEditExits
        saving={saving}
        onUpdate={handleUpdate}
        onSaveAsNew={() => setShowSaveAsNew(true)}
        onDiscard={onClear}
      />
      {usage && (
        <AlertDialog
          intent={Intent.WARNING}
          show={showFanOutConfirm}
          onHide={() => setShowFanOutConfirm(false)}
          title="Update everywhere it's used?"
          body={
            <>
              This chart is on <strong>{describeUsage(usage)}</strong> — updating changes it for
              everyone who sees them.
            </>
          }
          primaryAction={{ onClick: performUpdate, label: 'Update anyway' }}
          secondaryAction={{ onClick: () => {} }}
          loading={saving}
          tertiaryAction={{
            onClick: () => setShowSaveAsNew(true),
            label: 'Save as a new chart',
            design: Design.NEUTRAL
          }}
        />
      )}
      <NoEditAccessDialog
        show={showNoEditAccess}
        onHide={() => setShowNoEditAccess(false)}
        onSaveAsNew={() => setShowSaveAsNew(true)}
        createdBy={envelope?.createdBy ?? null}
      />
      <SaveChartAsNewDialog
        show={showSaveAsNew}
        defaultName={workingCopy.title ?? ''}
        loading={saving}
        onCancel={() => setShowSaveAsNew(false)}
        onSave={handleSaveAsNew}
        // "Show it here" repoints this card, which saves the dashboard. Withhold it when the user
        // may not change the dashboard — the copy itself is still allowed, it just lands in the
        // library instead.
        allowRepoint={canEditDashboard}
      />
    </>
  );
};
